// ============================================================================
// JARVISTRAVEL - FI PACING STRIP
// A day-by-day "pace at a glance" gauge: one bar per day, height + color on the
// fatigue scale (blue → green → amber → red). Heavy days literally stand out —
// taller and warmer — so the shape of the trip is readable in a second.
// Shared by Home (trip pacing card) and the Trip Plan header.
// ============================================================================

import { memo } from 'react';
import { dayScore } from '@/app/utils/fiEngine';
import { fiToneForScore } from '@/app/utils/fi';
import type { ItineraryDay } from '@/app/types';
import { fromLocalYmd } from '@/app/utils/dateOnly';

const dayNum = (date: string) => fromLocalYmd(date).toLocaleDateString('en-US', { day: 'numeric' });

export const FIPacingStrip = memo(function FIPacingStrip({ days, activeId, onSelectDay, height = 'h-14', className = '' }: {
  days: ItineraryDay[];
  /** Highlight the current day's column. */
  activeId?: string;
  /** Tap a day to jump to it. */
  onSelectDay?: (day: ItineraryDay) => void;
  /** Tailwind height class for the bars (default h-14). */
  height?: string;
  className?: string;
}) {
  if (days.length === 0) return null;
  const scored = days.map((d) => ({ d, score: dayScore(d) }));

  return (
    <div className={`flex items-end gap-1.5 ${className}`} role="img" aria-label="Day-by-day fatigue across the trip">
      {scored.map(({ d, score }) => {
        const tone = fiToneForScore(score);
        const active = d.id === activeId;
        const fill = Math.max(14, (score / 9) * 100);
        const inner = (
          <>
            <div className={`w-full ${height} rounded-md bg-surface-2 flex items-end overflow-hidden ring-1 transition-all ${active ? 'ring-accent/40' : 'ring-transparent'} ${onSelectDay ? 'group-hover:ring-border' : ''}`}>
              <div className={`w-full ${tone.solid} rounded-md transition-all duration-500`} style={{ height: `${fill}%` }} />
            </div>
            <span className={`text-[10px] font-medium tabular-nums transition-colors ${active ? 'text-accent' : onSelectDay ? 'text-muted group-hover:text-accent' : 'text-muted'}`}>{dayNum(d.date)}</span>
          </>
        );
        const base = 'flex-1 min-w-0 flex flex-col items-center gap-1';
        return onSelectDay ? (
          <button
            key={d.id}
            onClick={() => onSelectDay(d)}
            aria-label={`${dayNum(d.date)}: fatigue ${score} of 9`}
            aria-current={active ? 'true' : undefined}
            className={`${base} group rounded-lg hover:-translate-y-0.5 active:scale-95 transition-transform`}
          >
            {inner}
          </button>
        ) : (
          <div key={d.id} className={base}>{inner}</div>
        );
      })}
    </div>
  );
});
